import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import escapeHtml from './utils/escapeHtml';
import copyText from './utils/copyText';

/**
 * Manager odpowiedzialny za obsługę magnet linków
 * WAŻNE: Magnet link NIE jest przechowywany w DOM - tylko token SHA256
 */
export default class MagnetLinkManager {
  constructor() {
    // Cache danych per token:postId
    this.dataCache = new Map();
    // Elementy DOM per token (do aktualizacji)
    this.elements = new Map();
  }

  /**
   * Czy cache jest włączony (sterowane ustawieniem admina).
   */
  cacheEnabled() {
    return app.forum.attribute('magnetCacheEnabled') !== false;
  }

  /**
   * Czas ważności cache w ms (z ustawień; domyślnie 5 minut).
   */
  cacheTtlMs() {
    const ttl = parseInt(app.forum.attribute('magnetCacheTtl'), 10);
    return (Number.isFinite(ttl) && ttl > 0 ? ttl : 300) * 1000;
  }

  /**
   * Czy odpowiedź API to „zajęte" — serwer odmówił scrapowania, bo wszystkie jego
   * sloty (scrape_max_concurrent) były w użyciu. Taki wynik nie trafia do cache.
   */
  static isBusy(data) {
    return !!(data && data.scrape && data.scrape.error_type === 'busy');
  }

  /**
   * Klucz cache uwzględniający postId (dla niestandardowych nazw)
   */
  getCacheKey(token, postId) {
    return postId ? "".concat(token, ":").concat(postId) : token;
  }

  /**
   * Inicjalizuj magnet link w elemencie DOM
   * @param {HTMLElement} element - Element DOM
   * @param {string} token - Token SHA256 (NIE magnet link!)
   * @param {number|null} postId - ID posta
   * @param {number|null} postUserId - ID autora posta
   */
  async initializeMagnetLink(element, token, postId, postUserId) {
    // Walidacja tokena (64 znaki hex)
    if (!token || !/^[a-f0-9]{64}$/i.test(token)) {
      this.renderError(element, 'Invalid token');
      return;
    }

    // Zapisz element do mapy (potrzebne do aktualizacji licznika klików dla
    // wszystkich kart z danym tokenem). Przy okazji wymiataj elementy odłączone
    // od DOM po nawigacji SPA, żeby Mapa nie rosła bez końca przez całą sesję
    // (audyt #14 — wcześniej nigdy nie była czyszczona).
    if (!this.elements.has(token)) {
      this.elements.set(token, new Set());
    }
    const tokenElements = this.elements.get(token);
    for (const el of tokenElements) {
      if (!el.isConnected) tokenElements.delete(el);
    }
    tokenElements.add(element);

    // Sprawdź uprawnienia gościa po stronie klienta
    if (app.session.user === null && !app.forum.attribute('magnetGuestVisible')) {
      this.renderGuestNotAllowed(element);
      return;
    }

    // Renderuj loading state
    this.renderLoading(element);
    try {
      // Pobierz dane z API (używając tokena)
      const data = await this.fetchMagnetData(token, postId);

      // Renderuj kompletny magnet link
      this.renderMagnetLink(element, token, data, postId, postUserId);

      // Serwer odpowiedział „zajęte" (wszystkie sloty scrapowania w użyciu) —
      // jedna cicha ponowna próba po chwili, o ile karta wciąż jest na stronie.
      if (MagnetLinkManager.isBusy(data)) {
        setTimeout(() => {
          if (!element.isConnected) return;
          this.fetchMagnetData(token, postId).then(fresh => {
            if (element.isConnected) this.renderMagnetLink(element, token, fresh, postId, postUserId);
          }).catch(() => {});
        }, MagnetLinkManager.BUSY_RETRY_MS + Math.floor(Math.random() * 2000));
      }
    } catch (error) {
      // Sprawdź czy to błąd uprawnień
      if (error.status === 403) {
        const response = error.response || {};
        if (response.error === 'guest_not_allowed') {
          this.renderGuestNotAllowed(element);
        } else if (response.error === 'email_not_confirmed') {
          this.renderEmailNotConfirmed(element);
        } else if (response.error === 'permission_denied') {
          this.renderPermissionDenied(element);
        } else {
          this.renderError(element, response.message || 'Access denied');
        }
        return;
      }
      console.error('Magnet Link Error:', error);
      this.renderError(element, 'Failed to load magnet info');
    }
  }

  /**
   * Pobierz dane magnetu z API używając tokena
   */
  async fetchMagnetData(token, postId, forceFresh) {
    if (forceFresh === void 0) {
      forceFresh = false;
    }
    const cacheKey = this.getCacheKey(token, postId);

    // Sprawdź cache (chyba że wymuszono świeże dane lub cache wyłączony)
    if (!forceFresh && this.cacheEnabled()) {
      const cached = this.dataCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTtlMs()) {
        return cached.data;
      }
    }
    const params = [];
    if (postId) params.push('post_id=' + postId);
    if (forceFresh) params.push('refresh=1');
    let url = app.forum.attribute('apiUrl') + '/magnet/info/' + token;
    if (params.length) url += '?' + params.join('&');
    const response = await app.request({
      method: 'GET',
      url: url,
      errorHandler: this.silentErrorHandler.bind(this)
    });

    // Zapisz do cache (jeśli włączony) — ale nie odpowiedź „zajęte" (wszystkie
    // serwerowe sloty scrapowania w użyciu): to stan chwilowy, nie wynik.
    if (this.cacheEnabled() && !MagnetLinkManager.isBusy(response)) {
      this.dataCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });
    }
    return response;
  }

  /**
   * Cichy handler błędów - nie pokazuje alertów
   */
  silentErrorHandler(error) {
    // Nie pokazuj alertów dla błędów 403
    if (error.status === 403) {
      throw error;
    }
    // Dla innych błędów też nie pokazuj alertów, tylko rzuć wyjątek
    throw error;
  }

  /**
   * Renderuj loading state
   */
  renderLoading(element) {
    element.innerHTML = "\n            <div class=\"MagnetLink-container MagnetLink-loading\">\n                <span class=\"MagnetLink-icon\">\n                    <i class=\"fas fa-magnet fa-spin\"></i>\n                </span>\n                <span class=\"MagnetLink-text\">".concat(app.translator.trans('tryhackx-magnet-link.forum.loading'), "</span>\n            </div>\n        ");
  }

  /**
   * Renderuj kompletny magnet link
   */
  renderMagnetLink(element, token, data, postId, postUserId) {
    if (!data.success) {
      // Sprawdź specjalne typy błędów
      if (data.error === 'email_not_confirmed') {
        this.renderEmailNotConfirmed(element);
        return;
      }
      this.renderError(element, data.message || 'Error loading data');
      return;
    }
    const originalName = data.name || 'Unknown';
    const displayName = data.custom_name || originalName;
    const hasCustomName = data.has_custom_name || false;
    const scrapeEnabled = app.forum.attribute('magnetScraperEnabled');
    const clickTracking = app.forum.attribute('magnetClickTracking');
    const renameEnabled = app.forum.attribute('magnetRenameEnabled');
    let statsHtml = '';

    // Statystyki scrapera lub komunikat o błędzie
    if (scrapeEnabled && data.scrape) {
      if (data.scrape.success) {
        statsHtml = "\n                    <div class=\"MagnetLink-stats\">\n                        <span class=\"MagnetLink-stat MagnetLink-seeds\">\n                            <i class=\"fas fa-arrow-up\"></i>\n                            <span>".concat(app.translator.trans('tryhackx-magnet-link.forum.seeds'), "</span>\n                            <strong>").concat(data.scrape.seeders, "</strong>\n                        </span>\n                        <span class=\"MagnetLink-stat MagnetLink-leeches\">\n                            <i class=\"fas fa-arrow-down\"></i>\n                            <span>").concat(app.translator.trans('tryhackx-magnet-link.forum.leeches'), "</span>\n                            <strong>").concat(data.scrape.leechers, "</strong>\n                        </span>\n                        <span class=\"MagnetLink-stat MagnetLink-completed\">\n                            <i class=\"fas fa-check\"></i>\n                            <span>").concat(app.translator.trans('tryhackx-magnet-link.forum.completed'), "</span>\n                            <strong>").concat(data.scrape.completed, "</strong>\n                        </span>\n                    </div>\n                ");
      } else if (data.scrape.error_type) {
        // Pokaż komunikat o błędzie trackerów
        const errorKey = 'tryhackx-magnet-link.forum.errors.' + data.scrape.error_type;
        const errorMessage = app.translator.trans(errorKey) || data.scrape.message;
        statsHtml = "\n                    <div class=\"MagnetLink-info\">\n                        <i class=\"fas fa-info-circle\"></i>\n                        <span>".concat(this.escapeHtml(errorMessage), "</span>\n                    </div>\n                ");
      }
    }

    // Rozmiar pliku
    let sizeHtml = '';
    if (data.file_size_formatted) {
      sizeHtml = "\n                <span class=\"MagnetLink-size\">\n                    <i class=\"fas fa-hdd\"></i>\n                    <span>".concat(this.escapeHtml(data.file_size_formatted), "</span>\n                </span>\n            ");
    }

    // Licznik kliknięć
    let clicksHtml = '';
    if (clickTracking && data.click_count !== undefined) {
      clicksHtml = "\n                <span class=\"MagnetLink-clicks\" data-token=\"".concat(token, "\">\n                    <i class=\"fas fa-mouse-pointer\"></i>\n                    <span>").concat(data.click_count, "</span>\n                </span>\n            ");
    }

    // Przycisk zmiany nazwy (tylko dla autora posta)
    let renameButtonHtml = '';
    if (renameEnabled && postId) {
      const currentUserId = app.session.user ? app.session.user.id() : null;
      const isAuthor = postUserId && currentUserId && String(postUserId) === String(currentUserId);
      if (isAuthor) {
        renameButtonHtml = "\n                    <button class=\"MagnetLink-rename Button Button--icon Button--link\" title=\"".concat(app.translator.trans('tryhackx-magnet-link.forum.rename.tooltip'), "\">\n                        <i class=\"fas fa-pen\"></i>\n                    </button>\n                ");
      }
    }

    // Główny HTML - UWAGA: brak magnet linka w HTML!
    element.innerHTML = "\n            <div class=\"MagnetLink-container\">\n                <div class=\"MagnetLink-header\">\n                    <span class=\"MagnetLink-icon\">\n                        <i class=\"fas fa-magnet\"></i>\n                    </span>\n                    <span class=\"MagnetLink-title\">\n                        <a href=\"#\" class=\"MagnetLink-name\" data-token=\"".concat(token, "\" data-post-id=\"").concat(postId || '', "\" title=\"").concat(this.escapeHtml(displayName), "\">\n                            ").concat(this.escapeHtml(displayName), "\n                        </a>\n                        ").concat(renameButtonHtml, "\n                    </span>\n                    ").concat(sizeHtml, "\n                    ").concat(clicksHtml, "\n                    <span class=\"MagnetLink-actions\">\n                        <button class=\"MagnetLink-copy Button Button--icon Button--link\" title=\"").concat(app.translator.trans('tryhackx-magnet-link.forum.copy_link'), "\">\n                            <i class=\"fas fa-copy\"></i>\n                        </button>\n                        <button class=\"MagnetLink-refresh Button Button--icon Button--link\" title=\"").concat(app.translator.trans('tryhackx-magnet-link.forum.refresh'), "\">\n                            <i class=\"fas fa-sync-alt\"></i>\n                        </button>\n                    </span>\n                </div>\n                ").concat(statsHtml, "\n            </div>\n        ");

    // Dodaj event listenery
    this.attachEventListeners(element, token, postId, postUserId);

    // Animacja rozwijania statystyk/info
    requestAnimationFrame(() => {
      const stats = element.querySelector('.MagnetLink-stats');
      if (stats) {
        stats.classList.add('MagnetLink-stats--visible');
      }
      const info = element.querySelector('.MagnetLink-info');
      if (info) {
        info.classList.add('MagnetLink-info--visible');
      }
    });
  }

  /**
   * Dołącz event listenery
   */
  attachEventListeners(element, token, postId, postUserId) {
    // Kliknięcie w link - pobiera magnet URI z API i otwiera
    const linkElement = element.querySelector('.MagnetLink-name');
    if (linkElement) {
      linkElement.addEventListener('click', async e => {
        e.preventDefault();
        await this.handleClick(token, postId, element);
      });
    }

    // Przycisk kopiowania
    const copyButton = element.querySelector('.MagnetLink-copy');
    if (copyButton) {
      copyButton.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleCopy(token, postId, element);
      });
    }

    // Przycisk odświeżenia
    const refreshButton = element.querySelector('.MagnetLink-refresh');
    if (refreshButton) {
      refreshButton.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleRefresh(element, token, postId, postUserId);
      });
    }

    // Przycisk zmiany nazwy
    const renameButton = element.querySelector('.MagnetLink-rename');
    if (renameButton) {
      renameButton.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.showRenameModal(token, postId, element, postUserId);
      });
    }
  }

  /**
   * Obsłuż kliknięcie - pobierz magnet URI z API i otwórz
   */
  async handleClick(token, postId, element) {
    try {
      // Wywołaj API click - zwraca magnet_uri
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/magnet/click',
        body: {
          token: token,
          post_id: postId
        }
      });
      if (response.success && response.magnet_uri) {
        // Aktualizuj licznik kliknięć
        if (response.click_count !== undefined) {
          this.updateClickCountDisplay(token, response.click_count);
        }

        // Otwórz magnet link
        window.location.href = response.magnet_uri;
      } else {
        console.error('Click error:', response.message);
      }
    } catch (error) {
      console.error('Click request error:', error);
      // Nie pokazuj błędu użytkownikowi - po prostu nie otwieraj linku
    }
  }

  /**
   * Obsłuż kopiowanie - pobierz magnet URI z API i skopiuj do schowka
   */
  async handleCopy(token, postId, element) {
    const copyBtn = element.querySelector('.MagnetLink-copy i');
    try {
      // Wywołaj API click - zwraca magnet_uri (nalicza kliknięcie)
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/magnet/click',
        body: {
          token: token,
          post_id: postId
        }
      });
      if (response.success && response.magnet_uri) {
        // Aktualizuj licznik kliknięć
        if (response.click_count !== undefined) {
          this.updateClickCountDisplay(token, response.click_count);
        }

        // Skopiuj do schowka (z zapasową ścieżką dla stron http / odmowy
        // przeglądarki — samo navigator.clipboard rzucało tam TypeError).
        if (!(await copyText(response.magnet_uri))) {
          console.error('Copy error: the browser refused the clipboard');
          return;
        }

        // Pokaż potwierdzenie - zmień ikonę na check
        if (copyBtn) {
          copyBtn.classList.remove('fa-copy');
          copyBtn.classList.add('fa-check');
          setTimeout(() => {
            copyBtn.classList.remove('fa-check');
            copyBtn.classList.add('fa-copy');
          }, 1500);
        }
      } else {
        console.error('Copy error:', response.message);
      }
    } catch (error) {
      console.error('Copy request error:', error);
    }
  }

  /**
   * Obsłuż odświeżenie
   */
  async handleRefresh(element, token, postId, postUserId) {
    // Wyczyść cache
    this.dataCache.delete(this.getCacheKey(token, postId));

    // Spinner na ikonie odświeżania
    const spinIcon = element.querySelector('.MagnetLink-refresh i');
    if (spinIcon) {
      spinIcon.classList.add('fa-spin');
    }
    try {
      // Wymuś świeże dane: pomiń cache frontendu i serwera (refresh=1)
      const data = await this.fetchMagnetData(token, postId, true);
      this.renderMagnetLink(element, token, data, postId, postUserId);
      const limited = data && data.refresh && data.refresh.limited;
      if (limited) {
        this.showRefreshLimited(data.refresh);
      }

      // Wyszarz przycisk na czas cooldownu (lub do końca limitu, gdy
      // zablokowano). To tylko feedback UX — limity egzekwuje serwer.
      const cooldown = limited ? Math.max(1, parseInt(data.refresh.retry_after, 10) || 1) : Math.max(0, parseInt(app.forum.attribute('magnetRefreshCooldown'), 10) || 0);
      if (cooldown > 0) {
        this.coolDownRefreshButton(element, cooldown);
      }
    } catch (error) {
      console.error('Refresh error:', error);
      if (spinIcon) {
        spinIcon.classList.remove('fa-spin');
      }
    }
  }

  /**
   * Dyskretny komunikat o ograniczeniu odświeżania (cooldown / limit per IP).
   */
  showRefreshLimited(info) {
    const seconds = Math.max(1, parseInt(info.retry_after, 10) || 1);
    const key = info.reason === 'quota' ? 'tryhackx-magnet-link.forum.refresh_limit.quota' : 'tryhackx-magnet-link.forum.refresh_limit.cooldown';
    let message;
    try {
      message = app.translator.trans(key, {
        seconds
      });
    } catch (e) {
      message = null;
    }
    if (!message) return;
    try {
      const alertKey = app.alerts.show({
        type: 'warning'
      }, message);
      setTimeout(() => {
        try {
          app.alerts.dismiss(alertKey);
        } catch (e) {}
      }, 4000);
    } catch (e) {}
  }

  /**
   * Tymczasowo wyszarza i blokuje przycisk odświeżania w aktualnej karcie.
   */
  coolDownRefreshButton(element, seconds) {
    const btn = element.querySelector('.MagnetLink-refresh');
    if (!btn) return;
    btn.classList.add('MagnetLink-refresh--cooldown');
    btn.disabled = true;
    setTimeout(() => {
      const current = element.querySelector('.MagnetLink-refresh');
      if (current) {
        current.classList.remove('MagnetLink-refresh--cooldown');
        current.disabled = false;
      }
    }, seconds * 1000);
  }

  /**
   * Pokaż modal zmiany nazwy
   */
  showRenameModal(token, postId, element, postUserId) {
    var _cached$data, _cached$data2, _cached$data3, _cached$data4;
    const cacheKey = this.getCacheKey(token, postId);
    const cached = this.dataCache.get(cacheKey);
    const currentName = (cached == null || (_cached$data = cached.data) == null ? void 0 : _cached$data.custom_name) || (cached == null || (_cached$data2 = cached.data) == null ? void 0 : _cached$data2.name) || '';
    const originalName = (cached == null || (_cached$data3 = cached.data) == null ? void 0 : _cached$data3.name) || '';
    const hasCustomName = (cached == null || (_cached$data4 = cached.data) == null ? void 0 : _cached$data4.has_custom_name) || false;

    // Utwórz overlay modala
    const overlay = document.createElement('div');
    overlay.className = 'MagnetRenameModal-overlay';
    overlay.innerHTML = "\n            <div class=\"MagnetRenameModal\">\n                <div class=\"MagnetRenameModal-header\">\n                    <h3>".concat(app.translator.trans('tryhackx-magnet-link.forum.rename.modal_title'), "</h3>\n                    <button class=\"MagnetRenameModal-close Button Button--icon Button--link\">\n                        <i class=\"fas fa-times\"></i>\n                    </button>\n                </div>\n                <div class=\"MagnetRenameModal-body\">\n                    <label>").concat(app.translator.trans('tryhackx-magnet-link.forum.rename.label'), "</label>\n                    <input type=\"text\" class=\"FormControl MagnetRenameModal-input\"\n                           value=\"").concat(this.escapeHtml(currentName), "\"\n                           maxlength=\"500\"\n                           placeholder=\"").concat(this.escapeHtml(originalName), "\">\n                    ").concat(hasCustomName ? "\n                        <button class=\"Button MagnetRenameModal-restore\">\n                            <i class=\"fas fa-undo\"></i>\n                            ".concat(app.translator.trans('tryhackx-magnet-link.forum.rename.restore'), "\n                        </button>\n                    ") : '', "\n                </div>\n                <div class=\"MagnetRenameModal-footer\">\n                    <button class=\"Button Button--primary MagnetRenameModal-save\">\n                        ").concat(app.translator.trans('tryhackx-magnet-link.forum.rename.save'), "\n                    </button>\n                    <button class=\"Button MagnetRenameModal-cancel\">\n                        ").concat(app.translator.trans('tryhackx-magnet-link.forum.rename.cancel'), "\n                    </button>\n                </div>\n            </div>\n        ");
    document.body.appendChild(overlay);

    // Focus na input
    const input = overlay.querySelector('.MagnetRenameModal-input');
    setTimeout(() => input.focus(), 50);

    // Zamknij modal — globalny listener Escape sprzątamy NIEZALEŻNIE od drogi
    // zamknięcia (przycisk × / Anuluj / klik w tło / zapis / Escape). Wcześniej
    // listener znikał tylko przy Escape, więc każde inne zamknięcie go wyciekało
    // na document przez całą sesję (audyt #15).
    const escHandler = e => {
      if (e.key === 'Escape') closeModal();
    };
    const closeModal = () => {
      document.removeEventListener('keydown', escHandler);
      overlay.remove();
    };
    overlay.querySelector('.MagnetRenameModal-close').addEventListener('click', closeModal);
    overlay.querySelector('.MagnetRenameModal-cancel').addEventListener('click', closeModal);
    overlay.addEventListener('click', e => {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', escHandler);

    // Zapisz nową nazwę
    overlay.querySelector('.MagnetRenameModal-save').addEventListener('click', async () => {
      const newName = input.value.trim();
      if (!newName) return;
      try {
        await app.request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/magnet/rename',
          body: {
            token,
            post_id: postId,
            custom_name: newName
          }
        });
        // Wyczyść cache i przerenderuj
        this.dataCache.delete(cacheKey);
        // Wyczyść też cache tooltipa dyskusji
        if (app.magnetTooltip) app.magnetTooltip.clearCache();
        const data = await this.fetchMagnetData(token, postId);
        this.renderMagnetLink(element, token, data, postId, postUserId);
        closeModal();
      } catch (error) {
        console.error('Rename error:', error);
      }
    });

    // Enter zapisuje
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        overlay.querySelector('.MagnetRenameModal-save').click();
      }
    });

    // Przywróć oryginalną nazwę
    const restoreBtn = overlay.querySelector('.MagnetRenameModal-restore');
    if (restoreBtn) {
      restoreBtn.addEventListener('click', async () => {
        try {
          await app.request({
            method: 'DELETE',
            url: app.forum.attribute('apiUrl') + '/magnet/rename',
            body: {
              token,
              post_id: postId
            }
          });
          this.dataCache.delete(cacheKey);
          // Wyczyść też cache tooltipa dyskusji
          if (app.magnetTooltip) app.magnetTooltip.clearCache();
          const data = await this.fetchMagnetData(token, postId);
          this.renderMagnetLink(element, token, data, postId, postUserId);
          closeModal();
        } catch (error) {
          console.error('Restore error:', error);
        }
      });
    }
  }

  /**
   * Aktualizuj wyświetlaną liczbę kliknięć dla wszystkich elementów z danym tokenem
   */
  updateClickCountDisplay(token, count) {
    const elements = this.elements.get(token);
    if (!elements) return;
    elements.forEach(el => {
      // Element odłączony od DOM (nawigacja SPA) — usuń z mapy i pomiń (audyt #14).
      if (!el.isConnected) {
        elements.delete(el);
        return;
      }
      const clicksSpan = el.querySelector('.MagnetLink-clicks span');
      if (clicksSpan) {
        clicksSpan.textContent = count;
        // Animacja
        const clicksContainer = clicksSpan.closest('.MagnetLink-clicks');
        if (clicksContainer) {
          clicksContainer.classList.add('MagnetLink-clicks--updated');
          setTimeout(() => {
            clicksContainer.classList.remove('MagnetLink-clicks--updated');
          }, 500);
        }
      }
    });

    // Po wymieceniu odłączonych elementów nie trzymaj pustego zbioru/klucza.
    if (elements.size === 0) this.elements.delete(token);
  }

  /**
   * Renderuj błąd
   */
  renderError(element, message) {
    element.innerHTML = "\n            <div class=\"MagnetLink-container MagnetLink-error\">\n                <span class=\"MagnetLink-icon\">\n                    <i class=\"fas fa-exclamation-triangle\"></i>\n                </span>\n                <span class=\"MagnetLink-text\">".concat(this.escapeHtml(message), "</span>\n            </div>\n        ");
    element.classList.add('MagnetLink-error');
  }

  /**
   * Renderuj komunikat dla gościa
   */
  renderGuestNotAllowed(element) {
    element.innerHTML = "\n            <div class=\"MagnetLink-container\">\n                <span class=\"MagnetLink-icon\">\n                    <i class=\"fas fa-lock\"></i>\n                </span>\n                <span class=\"MagnetLink-text\">\n                    ".concat(app.translator.trans('tryhackx-magnet-link.forum.guest_not_allowed'), "\n                    <a href=\"").concat(app.forum.attribute('baseUrl'), "/login\">\n                        ").concat(app.translator.trans('tryhackx-magnet-link.forum.login'), "\n                    </a>\n                    ").concat(app.translator.trans('tryhackx-magnet-link.forum.or'), "\n                    <a href=\"").concat(app.forum.attribute('baseUrl'), "/register\">\n                        ").concat(app.translator.trans('tryhackx-magnet-link.forum.register'), "\n                    </a>\n                </span>\n            </div>\n        ");
    element.classList.add('MagnetLink-guest');
  }

  /**
   * Renderuj komunikat dla niepotwierdzonych użytkowników
   */
  renderEmailNotConfirmed(element) {
    element.innerHTML = "\n            <div class=\"MagnetLink-container\">\n                <span class=\"MagnetLink-icon\">\n                    <i class=\"fas fa-envelope\"></i>\n                </span>\n                <span class=\"MagnetLink-text\">\n                    ".concat(app.translator.trans('tryhackx-magnet-link.forum.email_not_confirmed'), "\n                </span>\n            </div>\n        ");
    element.classList.add('MagnetLink-guest');
  }

  /**
   * Renderuj komunikat dla użytkowników bez uprawnień
   */
  renderPermissionDenied(element) {
    element.innerHTML = "\n            <div class=\"MagnetLink-container\">\n                <span class=\"MagnetLink-icon\">\n                    <i class=\"fas fa-ban\"></i>\n                </span>\n                <span class=\"MagnetLink-text\">\n                    ".concat(app.translator.trans('tryhackx-magnet-link.forum.permission_denied'), "\n                </span>\n            </div>\n        ");
    element.classList.add('MagnetLink-guest');
  }

  /**
   * Escape HTML — also for attribute values (quotes included), see utils/escapeHtml.
   */
  escapeHtml(text) {
    return escapeHtml(text);
  }
}
/** Po ilu ms ponowić wczytanie karty, gdy serwer odpowiedział „zajęte" (+ losowe 0–2 s). */
_defineProperty(MagnetLinkManager, "BUSY_RETRY_MS", 4000);